import { useCallback, useEffect, useMemo } from "react";
import { zodResolver } from "@hookform/resolvers/zod";
import { observer } from "mobx-react-lite";
import { FormProvider, useForm } from "react-hook-form";
import { z } from "zod";
import { Button, Modal, TextArea, Input } from "@synthesis/shared-ui";

import { AdminModalBody, AdminModalFooter } from "../../../components/admin-console/admin-modal";
import { type DocumentTypeFormValues, usePageStore } from "../reference-document-types-page.lib";

const documentTypeSchema = z.object({
  name: z.string().trim().min(1, "Укажите наименование"),
  description: z.string().trim().optional(),
});

const EMPTY_FORM: DocumentTypeFormValues = { name: "", description: "" };

/** Модалки «Новый тип документа» / «Редактирование типа документа». Открываются через стор. Бэка нет — сохранение только закрывает окно. */
export const DocumentTypeModal = observer(() => {
  const store = usePageStore()!;
  const action = store.popupAction;
  const isOpen = action?.type === "add-document-type" || action?.type === "edit-document-type";
  const isEdit = action?.type === "edit-document-type";
  const documentType = action?.type === "edit-document-type" ? action.documentType : undefined;

  const defaults = useMemo<DocumentTypeFormValues>(
    () => (documentType ? { name: documentType.name, description: documentType.description } : EMPTY_FORM),
    [documentType],
  );
  const methods = useForm<DocumentTypeFormValues>({ resolver: zodResolver(documentTypeSchema), mode: "onChange", defaultValues: defaults });

  // При каждом открытии — префилл формы (для «Редактирования» данные выбранного типа).
  useEffect(() => {
    if (isOpen) {
      methods.reset(defaults);
    }
  }, [defaults, isOpen, methods]);

  const handleClose = useCallback(() => {
    methods.reset(EMPTY_FORM);
    store.setPopupAction(null);
  }, [methods, store]);

  return (
    <Modal.Root isOpen={isOpen} setIsOpen={(open) => (open ? null : handleClose())}>
      <Modal.Content className="w-[43.5rem] max-w-[90vw]">
        <Modal.Title>{isEdit ? "Редактирование типа документа" : "Новый тип документа"}</Modal.Title>
        <FormProvider {...methods}>
          <AdminModalBody>
            <h4 className="heading-h4 text-type-common">{isEdit ? "Редактирование типа документа" : "Новый тип документа"}</h4>

            <div className="flex flex-col gap-4">
              <Input name="name" label="Наименование" required placeholder="Наименование типа" />
              <TextArea name="description" label="Описание" placeholder="Что это за документ и какие к нему требования" />
            </div>
          </AdminModalBody>

          <AdminModalFooter className={isEdit ? "justify-between" : undefined}>
            {isEdit ? (
              <>
                {/* TODO(FRONT-1390): сохранение/удаление типа документа — после появления бэкенда. */}
                <Button theme="primary" size="lg" onClick={methods.handleSubmit(handleClose)}>
                  Сохранить
                </Button>
                <Button theme="secondary" size="lg" onClick={handleClose}>
                  Удалить
                </Button>
              </>
            ) : (
              <div className="flex items-center gap-4">
                {/* TODO(FRONT-1390): добавление типа документа — после появления бэкенда. */}
                <Button theme="primary" size="lg" onClick={methods.handleSubmit(handleClose)}>
                  Добавить
                </Button>
                <Button theme="secondary" size="lg" onClick={handleClose}>
                  Отмена
                </Button>
              </div>
            )}
          </AdminModalFooter>
        </FormProvider>
      </Modal.Content>
    </Modal.Root>
  );
});
